/** CEO & Leadership Team vs PE & Board — styled HTML for editor + sent email.
 *  Classic 5X copy stays for pack=5x; tenants use PACK_GENERIC (no AdvantageCEO/PE). */

export const EMAIL_TEMPLATE_LEADERSHIP = 1;
export const EMAIL_TEMPLATE_PE_BOARD = 2;
export const ASSESSMENT_MODE_LITE_TO_FULL = "lite_to_full";
export const ASSESSMENT_MODE_FULL_ONLY = "full_only";

export const PACK_5X = "5x";
export const PACK_GENERIC = "generic";

export const PE_BOARD_ROLES = new Set(["PE Sponsor", "Board Member"]);
export const LEADERSHIP_SIGN_OFF = "The 5x Assessment Team";
export const GENERIC_SIGN_OFF = "Company Team";

const BRAND = "#004f73";
const TEXT = "#1f2937";
const MUTED = "#4b5563";
const FONT =
  "Arial, Helvetica, 'Segoe UI', system-ui, -apple-system, sans-serif";

/** Single line break between paragraphs */
const GAP = "<br />";

const S = {
  wrap: `font-family:${FONT};max-width:640px;color:${TEXT};`,
  greeting: `margin:0;font-size:17px;line-height:1.5;color:${TEXT};`,
  p: `margin:0;font-size:15px;line-height:1.7;color:${TEXT};`,
  sign: `margin:0;font-size:15px;line-height:1.6;color:${TEXT};`,
};

function p(html) {
  return `<p style="${S.p}">${html}</p>${GAP}`;
}

export function normalizeEmailPack(pack) {
  return pack === PACK_GENERIC ? PACK_GENERIC : PACK_5X;
}

export function inviteSubjectForPack(pack, { isResend = false } = {}) {
  if (normalizeEmailPack(pack) === PACK_GENERIC) {
    return isResend ? GENERIC_RESEND_SUBJECT : GENERIC_INVITE_SUBJECT;
  }
  return isResend ? DEFAULT_RESEND_SUBJECT : DEFAULT_INVITE_SUBJECT;
}

function importantSection() {
  return `<div style="background:#f8fafc;border-left:4px solid ${BRAND};padding:20px 22px;border-radius:0 10px 10px 0;">
<p style="margin:0 0 12px;font-size:12px;font-weight:700;color:${BRAND};letter-spacing:0.06em;text-transform:uppercase;">Important</p>
<p style="margin:0 0 14px;font-size:15px;font-weight:700;line-height:1.5;color:${TEXT};">Before starting, please keep the following in mind:</p>
<p style="margin:0 0 10px;font-size:15px;line-height:1.65;color:${TEXT};">You will be asked to rate each statement on <u>two dimensions</u>:</p>
<ul style="margin:0 0 14px;padding-left:20px;font-size:15px;line-height:1.75;color:${TEXT};">
<li style="margin-bottom:10px;"><strong>Level of Importance –</strong> <span style="color:${MUTED};">How critical is this statement to the company’s success?</span></li>
<li><strong>Level of Agreement –</strong> <span style="color:${MUTED};">How well do you believe the organization is currently performing or executing relative to the statement?</span></li>
</ul>
<p style="margin:0 0 14px;font-size:14px;line-height:1.6;color:${MUTED};"><strong style="color:${TEXT};">Note:</strong> if you do not have sufficient knowledge or observable data to make an assessment, please select “Unable to Assess.”</p>
<p style="margin:0;font-size:15px;line-height:1.7;color:${TEXT};">Please base your responses and evaluation on the current state of the business. Your perspective across these two dimensions helps pinpoint the most important friction points and where leadership focus will have the greatest impact.</p>
</div>${GAP}`;
}

function buildInviteEmailHtml(firstName, lastName, bodyParagraphs, { signOff = LEADERSHIP_SIGN_OFF } = {}) {
  const blocks = [
    `<p style="${S.greeting}"><strong>Hello ${firstName} ${lastName},</strong></p>`,
    p(
      `You are invited to complete the <strong style="color:${BRAND};">5x Team Assessment</strong>, based on AdvantageCEO’s two-year research study of how top private equity leadership teams accelerate value and maximize returns—as featured in the book <em>The 5x CEO</em>.`
    ),
    p(
      `The assessment is organized into <strong>six sections</strong> covering the five pillars of the 5x CEO model, plus one section on overall financial and investment performance.`
    ),
    importantSection(),
    ...bodyParagraphs.map((html) => p(html)),
    p(
      `Please use the unique link below to begin your assessment. This link is unique to you—<strong>please do not forward it</strong>, as doing so could invalidate your responses.`
    ),
    p(`Thank you for your time and valuable input.`),
    `<p style="${S.sign}">Sincerely,<br /><strong style="color:${BRAND};font-size:16px;">${signOff}</strong></p>`,
  ];
  return `<div class="invite-email-body" style="${S.wrap}">${blocks.join("")}</div>`;
}

function buildGenericInviteEmailHtml(firstName, lastName, bodyParagraphs, productName = "Team Assessment") {
  const blocks = [
    `<p style="${S.greeting}"><strong>Hello ${firstName} ${lastName},</strong></p>`,
    p(
      `You are invited to complete the <strong style="color:${BRAND};">${productName}</strong>. Your responses help surface strengths and priority improvement opportunities.`
    ),
    importantSection(),
    ...bodyParagraphs.map((html) => p(html)),
    p(
      `Please use the unique link below to begin your assessment. This link is unique to you—<strong>please do not forward it</strong>, as doing so could invalidate your responses.`
    ),
    p(`Thank you for your time and valuable input.`),
    `<p style="${S.sign}">Sincerely,<br /><strong style="color:${BRAND};font-size:16px;">${GENERIC_SIGN_OFF}</strong></p>`,
  ];
  return `<div class="invite-email-body" style="${S.wrap}">${blocks.join("")}</div>`;
}

function normalizeAssessmentMode(mode) {
  return mode === ASSESSMENT_MODE_FULL_ONLY
    ? ASSESSMENT_MODE_FULL_ONLY
    : ASSESSMENT_MODE_LITE_TO_FULL;
}

function buildLeadershipEmailHtml(firstName, lastName, assessmentMode) {
  const mode = normalizeAssessmentMode(assessmentMode);
  const flowParagraphs =
    mode === ASSESSMENT_MODE_FULL_ONLY
      ? [
          `You will begin directly with the <strong>full 5x Team Assessment</strong> for a complete organizational evaluation across all pillars and financial performance.`,
          `Please plan approximately <strong>30 minutes</strong> to complete the assessment in one sitting, or return later using the same invitation link.`,
        ]
      : [
          `To make getting started easier, your assessment journey begins with a short <strong>LITE Assessment</strong> designed to capture the most important insights in just a few minutes.`,
          `Once completed, you’ll automatically unlock the complete 5x Team Assessment for a deeper organizational evaluation. You may choose to <strong>continue immediately</strong> or <strong>return later</strong> using the same invitation link.`,
          `Even the initial LITE Assessment provides <strong>meaningful leadership insight</strong>, so we encourage you to complete it right away. A few minutes of thoughtful input can help uncover key priorities, alignment gaps, and opportunities for greater organizational performance.`,
        ];
  return buildInviteEmailHtml(firstName, lastName, [
    ...flowParagraphs,
    `All responses are <strong>anonymous</strong>, and results are reported only in aggregate for the Board &amp; PE team members.`,
  ]);
}

function buildPeBoardEmailHtml(firstName, lastName, assessmentMode) {
  const mode = normalizeAssessmentMode(assessmentMode);
  const flowParagraphs =
    mode === ASSESSMENT_MODE_FULL_ONLY
      ? [
          `You will begin directly with the <strong>full 5x Team Assessment</strong> for a complete organizational and financial evaluation.`,
          `Please plan approximately <strong>30 minutes</strong> to complete the assessment, with the option to pause and continue later using the same invitation link.`,
        ]
      : [
          `To make participation easier, your assessment journey will begin with a short <strong>LITE Assessment</strong> focused on capturing the most critical organizational and leadership insights in just a few minutes.`,
          `Once completed, you’ll automatically gain access to the complete 5x Team Assessment for a deeper evaluation. You may <strong>continue immediately</strong> or <strong>return later</strong> using the same invitation link.`,
        ];
  return buildInviteEmailHtml(firstName, lastName, [
    ...flowParagraphs,
    mode === ASSESSMENT_MODE_FULL_ONLY
      ? `Your perspective as a <strong>Board / PE participant</strong> is especially valuable in helping identify strategic alignment gaps, execution risks, and opportunities to accelerate enterprise value creation.`
      : `Your perspective as a <strong>Board / PE participant</strong> is especially valuable in helping identify strategic alignment gaps, execution risks, and opportunities to accelerate enterprise value creation. Even the initial LITE Assessment can provide meaningful directional insight, so we <strong>strongly encourage you to complete it right away</strong>.`,
    `All responses are <strong>anonymous</strong>, and results are reported only in aggregate.`,
  ]);
}

function buildGenericLeadershipEmailHtml(
  firstName,
  lastName,
  assessmentMode,
  productName = "Team Assessment"
) {
  const mode = normalizeAssessmentMode(assessmentMode);
  const flowParagraphs =
    mode === ASSESSMENT_MODE_FULL_ONLY
      ? [
          `You will begin directly with the <strong>full ${productName}</strong> for a complete organizational evaluation.`,
          `Please plan approximately <strong>30 minutes</strong> to complete the assessment in one sitting, or return later using the same invitation link.`,
        ]
      : [
          `To make getting started easier, your assessment journey begins with a short <strong>LITE Assessment</strong> designed to capture the most important insights in just a few minutes.`,
          `Once completed, you can continue with the full assessment immediately or return later using the same invitation link.`,
        ];
  return buildGenericInviteEmailHtml(
    firstName,
    lastName,
    [
      ...flowParagraphs,
      `All responses are <strong>anonymous</strong>, and results are reported only in aggregate.`,
    ],
    productName
  );
}

export function templateForRole(designation) {
  return PE_BOARD_ROLES.has(designation)
    ? EMAIL_TEMPLATE_PE_BOARD
    : EMAIL_TEMPLATE_LEADERSHIP;
}

export function emailTemplateLabel(templateId) {
  return templateId === EMAIL_TEMPLATE_PE_BOARD
    ? "PE & Board Participants"
    : "CEO & Leadership Team";
}

export function suggestEmailTemplate(participants) {
  const roles = (participants || [])
    .map((p) => p.designation)
    .filter(Boolean);
  if (!roles.length) return EMAIL_TEMPLATE_LEADERSHIP;
  const peCount = roles.filter((r) => PE_BOARD_ROLES.has(r)).length;
  return peCount > roles.length / 2
    ? EMAIL_TEMPLATE_PE_BOARD
    : EMAIL_TEMPLATE_LEADERSHIP;
}

export function buildDefaultEmailHtml(
  templateId,
  { firstName, lastName },
  {
    assessmentMode = ASSESSMENT_MODE_LITE_TO_FULL,
    pack = PACK_5X,
    productName = "Team Assessment",
  } = {}
) {
  if (normalizeEmailPack(pack) === PACK_GENERIC) {
    return buildGenericLeadershipEmailHtml(
      firstName,
      lastName,
      assessmentMode,
      productName
    );
  }
  if (templateId === EMAIL_TEMPLATE_PE_BOARD) {
    return buildPeBoardEmailHtml(firstName, lastName, assessmentMode);
  }
  return buildLeadershipEmailHtml(firstName, lastName, assessmentMode);
}

/** Reminder / resend copy — legacy EmailEditor3 "As a reminder..." */
export function buildResendEmailHtml(
  templateId,
  { firstName, lastName },
  {
    assessmentMode = ASSESSMENT_MODE_LITE_TO_FULL,
    pack = PACK_5X,
    productName = "Team Assessment",
  } = {}
) {
  if (normalizeEmailPack(pack) === PACK_GENERIC) {
    const greeting = `<p style="${S.greeting}"><strong>Hello ${firstName} ${lastName},</strong></p>`;
    const blocks = [
      greeting,
      p(
        `As a <strong>reminder</strong>, you are invited to complete the <strong style="color:${BRAND};">${productName}</strong>.`
      ),
      importantSection(),
      p(
        `<strong>Honesty matters.</strong> Your candid input helps identify where the organization is strong and where improvement is needed. All responses are <strong>anonymous</strong>, and results are reported in aggregate.`
      ),
      p(
        `Please use the unique link below to begin your assessment. This link is unique to you—<strong>please do not forward it</strong>, as doing so could invalidate your responses.`
      ),
      p(`Thank you for your time and valuable input.`),
      `<p style="${S.sign}">Sincerely,<br /><strong style="color:${BRAND};font-size:16px;">${GENERIC_SIGN_OFF}</strong></p>`,
    ];
    return `<div class="invite-email-body" style="${S.wrap}">${blocks.join("")}</div>`;
  }

  const mode = normalizeAssessmentMode(assessmentMode);
  const greeting = `<p style="${S.greeting}"><strong>Hello ${firstName} ${lastName},</strong></p>`;
  const reminderLead = p(
    mode === ASSESSMENT_MODE_FULL_ONLY
      ? `As a <strong>reminder</strong>, you are invited to complete the <strong style="color:${BRAND};">full 5x Team Assessment</strong>, based on AdvantageCEO's two-year research study of how top private equity leadership teams accelerate value and maximize returns—as featured in the book <em>The 5x CEO</em>.`
      : `As a <strong>reminder</strong>, you are invited to complete the <strong style="color:${BRAND};">5x Team Assessment</strong>, based on AdvantageCEO's two-year research study of how top private equity leadership teams accelerate value and maximize returns—as featured in the book <em>The 5x CEO</em>.`
  );
  const sections = p(
    `The assessment is organized into <strong>six sections</strong> covering the five pillars of the 5x CEO model, plus one section on overall financial and investment performance.`
  );
  const tail =
    templateId === EMAIL_TEMPLATE_PE_BOARD
      ? [
          p(
            `Your perspective as a <strong>Board / PE participant</strong> is especially valuable in helping identify strategic alignment gaps, execution risks, and opportunities to accelerate enterprise value creation.`
          ),
        ]
      : [];
  const blocks = [
    greeting,
    reminderLead,
    sections,
    importantSection(),
    p(
      `The assessment takes about <strong>30 minutes</strong> to complete. Be thoughtful, but don't overthink your answers.`
    ),
    p(
      `<strong>Honesty matters.</strong> Your candid input helps identify where the organization is strong and where improvement is needed. All responses are <strong>anonymous</strong>, and results are reported in aggregate.`
    ),
    ...tail,
    p(
      `Please use the unique link below to begin your assessment. This link is unique to you—<strong>please do not forward it</strong>, as doing so could invalidate your responses.`
    ),
    p(`Thank you for your time and valuable input.`),
    `<p style="${S.sign}">Sincerely,<br /><strong style="color:${BRAND};font-size:16px;">${LEADERSHIP_SIGN_OFF}</strong></p>`,
  ];
  return `<div class="invite-email-body" style="${S.wrap}">${blocks.join("")}</div>`;
}

export function buildResendEmailHtmlForSend(
  templateId,
  {
    assessmentMode = ASSESSMENT_MODE_LITE_TO_FULL,
    pack = PACK_5X,
    productName = "Team Assessment",
  } = {}
) {
  return buildResendEmailHtml(
    templateId,
    {
      firstName: "{{first_name}}",
      lastName: "{{last_name}}",
    },
    { assessmentMode, pack, productName }
  );
}

export function buildDefaultEmailHtmlForSend(
  templateId,
  {
    assessmentMode = ASSESSMENT_MODE_LITE_TO_FULL,
    pack = PACK_5X,
    productName = "Team Assessment",
  } = {}
) {
  return buildDefaultEmailHtml(
    templateId,
    {
      firstName: "{{first_name}}",
      lastName: "{{last_name}}",
    },
    { assessmentMode, pack, productName }
  );
}

export function previewNameFromParticipants(participants) {
  const row = (participants || []).find(
    (p) =>
      p.full_name?.trim() ||
      p.first_name?.trim() ||
      p.email?.trim()
  );
  if (!row) return { firstName: "Participant", lastName: "" };
  if (row.first_name?.trim()) {
    return {
      firstName: row.first_name.trim(),
      lastName: (row.last_name || "").trim(),
    };
  }
  const trimmed = (row.full_name || "").trim();
  if (!trimmed) return { firstName: "Participant", lastName: "" };
  const parts = trimmed.split(/\s+/);
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
}

export function wrapEmailHtmlForApi(editorHtml) {
  const inner = editorHtml.includes("invite-email-body")
    ? editorHtml
    : `<div class="invite-email-body" style="font-family:${FONT};max-width:640px;color:${TEXT};">${editorHtml}</div>`;
  return `<div class="content" style="margin:0;padding:24px 16px;background:#f3f4f6;font-family:${FONT};">
<div style="max-width:680px;margin:0 auto;background:#ffffff;border-radius:12px;padding:32px 28px;border:1px solid #e5e7eb;">
${inner}
</div></div>`;
}

export const DEFAULT_INVITE_SUBJECT = "Invitation: 5x Team Assessment";
export const DEFAULT_RESEND_SUBJECT = "Reminder: 5x Team Assessment";
export const GENERIC_INVITE_SUBJECT = "Invitation: Team Assessment";
export const GENERIC_RESEND_SUBJECT = "Reminder: Team Assessment";
